/**
 * BUSCAR EN EL IMPRESO LO QUE UNA FUENTE PROBADA YA DIJO.
 *
 * `frente.ts` lee a ciegas: encuentra el rótulo y mira debajo. Eso necesita que
 * el rótulo se lea, y hay documentos donde no se lee. Medido sobre el frente de
 * 2010, cuyos rótulos están impresos chicos y con las letras separadas: el OCR
 * devolvió "Tir" por "APELLIDO/S:", "SEEN PA el A IY" por "NACIONALIDAD:" y
 * nada por "NOMBRE/S:" — y en la misma pasada leyó perfectos los valores
 * ROVIRA, RODOLFO y MANUEL. Subir la escala no lo arregla: probado a 1161,
 * 1600, 2000, 2400, 2800 y 3200 px, cero rótulos en todas.
 *
 * Acá la búsqueda va al revés. El MRZ o el código 2D ya dijeron, con prueba,
 * que el apellido es ROVIRA. Buscar "ROVIRA" en las palabras del OCR es un
 * problema mucho más fácil que descubrirlo, y tolera el ruido: no hace falta
 * que el rótulo exista.
 *
 * Qué agrega, y qué NO agrega:
 *
 *  - Agrega la CAJA: dónde está ese dato en la foto. Es lo que permite marcarlo
 *    y es el objetivo 2 —que un empleado de SADAIC pueda corroborarlo mirando—.
 *  - Agrega una segunda lectura independiente. Si el OCR leyó exactamente lo
 *    mismo, dos fuentes que fallan distinto coinciden, y eso vale para los
 *    campos que NINGÚN dígito de control cubre: apellido, nombres, sexo y
 *    nacionalidad.
 *  - NO agrega prueba por sí solo. Se buscó lo que ya se creía, así que el
 *    hallazgo reporta lo que el OCR leyó ahí —no lo que se buscó—, y la
 *    coincidencia la decide el consolidador. Si el OCR leyó otra cosa, eso es
 *    un desacuerdo y se muestra como tal.
 */
import { PalabraOcr } from "../dniMatch";
import { Hallazgo } from "./hallazgos";
import { Rectangulo } from "./types";
import { fechaDelFrente, filas } from "./frente";
import { distancia } from "./texto";

/** Un dato que una fuente probada ya entregó, y que se quiere ubicar impreso. */
export interface ValorEsperado {
  campo: string;
  valor: string;
}

const norm = (t: string) =>
  t.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** Los tres meses como los imprime el DNI, en español y en inglés. */
const MES_IMPRESO: [string, string][] = [
  ["ENE", "JAN"], ["FEB", "FEB"], ["MAR", "MAR"], ["ABR", "APR"],
  ["MAY", "MAY"], ["JUN", "JUN"], ["JUL", "JUL"], ["AGO", "AUG"],
  ["SEP", "SEP"], ["OCT", "OCT"], ["NOV", "NOV"], ["DIC", "DEC"],
];

/**
 * En qué formas puede aparecer IMPRESO un valor que vino del MRZ o del código.
 *
 * Las fechas son el caso que obliga a esto: el MRZ las entrega como
 * "1963-10-21" y el frente las imprime "21 OCT/OCT 1963" o "21.10.1963" según
 * el modelo. Buscar la forma ISO no encontraría ninguna.
 */
export function formasImpresas(valor: string): string[] {
  const iso = valor.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const [, anio, mes, dia] = iso;
    const [es, en] = MES_IMPRESO[parseInt(mes, 10) - 1] ?? ["", ""];
    return [
      `${dia}${es}${anio}`,
      `${dia}${es}${en}${anio}`,
      `${dia}${mes}${anio}`,
    ];
  }
  const n = norm(valor);
  return n ? [n] : [];
}

/**
 * Cuánto ruido se le tolera para dar por ENCONTRADO un valor.
 *
 * Una diferencia cada cuatro caracteres: tres cuartos del texto tienen que
 * coincidir. Es una proporción, no un número ajustado a nuestras fotos, y se
 * mide contra el más largo de los dos para que agregar basura tampoco pase.
 *
 * Por qué no es más estricto: el OCR de una foto blanda parte los números
 * —medido, "27.391.444" salió "27.391" y "4545"— y con una sola diferencia
 * permitida el dato no se ubica nunca.
 *
 * Por qué esto no afloja la verificación: acá solo se decide DÓNDE está el
 * dato. El valor que se reporta es el que leyó el OCR ahí, y para que dos
 * fuentes cuenten como coincidentes el consolidador exige que digan lo mismo,
 * carácter por carácter.
 */
export function toleranciaDe(forma: string, encontrado: string): number {
  return Math.max(1, Math.floor(Math.max(forma.length, encontrado.length) / 4));
}

/** Hasta cuántas palabras seguidas se prueban como un mismo valor. */
const MAX_PALABRAS = 4;
/** Menos de esto no se busca: un texto corto coincide con cualquier cosa. */
const MIN_CARACTERES = 4;
/**
 * Un valor abreviado puede estar impreso completo: el MRZ dice "ARG" y la
 * tarjeta dice "ARGENTINA". El consolidador ya sabe qué hacer con un prefijo
 * —no compite, acompaña—, así que acá alcanza con encontrarlo.
 */
const MIN_PARA_PREFIJO = 5;
/**
 * Y no mucho más larga que la forma. Sin este techo, "WOLFRAM" coincidía con la
 * línea entera del MRZ por ser su principio.
 */
const MAX_ESTIRAMIENTO = 3;
const MAX_LETRAS_DE_MAS = 8;
/** Un token impreso nunca llega a esto; los del MRZ arrancan en 25. */
const LARGO_DE_TOKEN_MRZ = 18;

/**
 * ¿Este renglón es el MRZ?
 *
 * El OCR del impreso pasa por TODA la cara, MRZ incluido, y ahí el apellido
 * está otra vez. Medido sobre un dorso real: "WOLFRAM" se encontraba
 * adentro de "WOLFRAMKELENAKLLLLLLLKLKLKLK" —la línea 3 leída por el motor
 * común— y se daba por confirmado contra sí mismo. Una fuente no se corrobora
 * sola: el MRZ ya entró como MRZ.
 *
 * Se reconoce por lo que lo hace único, no por dónde está: renglones de un solo
 * token larguísimo, y el relleno '<'.
 */
function esRenglonDeMrz(fila: PalabraOcr[]): boolean {
  if (fila.some((w) => norm(w.text).length >= LARGO_DE_TOKEN_MRZ)) return true;
  return fila.some((w) => w.text.includes("<<"));
}

function unir(ws: PalabraOcr[]): Rectangulo {
  const x = Math.min(...ws.map((w) => w.bbox.x0));
  const y = Math.min(...ws.map((w) => w.bbox.y0));
  return {
    x,
    y,
    w: Math.max(...ws.map((w) => w.bbox.x1)) - x,
    h: Math.max(...ws.map((w) => w.bbox.y1)) - y,
  };
}

interface Encontrado {
  palabras: PalabraOcr[];
  texto: string;
  normalizado: string;
  distancia: number;
}

/** La corrida de palabras que más se parece a alguna de las formas. */
function buscarForma(renglones: PalabraOcr[][], formas: string[]): Encontrado | null {
  let mejor: Encontrado | null = null;
  for (const fila of renglones) {
    for (let i = 0; i < fila.length; i++) {
      for (let j = i; j < Math.min(i + MAX_PALABRAS, fila.length); j++) {
        const corrida = fila.slice(i, j + 1);
        const texto = corrida.map((w) => w.text.trim()).join(" ").trim();
        const nrm = norm(texto);
        if (nrm.length < MIN_CARACTERES) continue;
        for (const forma of formas) {
          const esPrefijo =
            nrm.length >= MIN_PARA_PREFIJO &&
            nrm.startsWith(forma) &&
            nrm.length <= forma.length * MAX_ESTIRAMIENTO &&
            nrm.length - forma.length <= MAX_LETRAS_DE_MAS;
          // Una forma corta —"ARG"— SOLO se acepta como principio de una
          // palabra larga. Buscarla por distancia coincidiría con cualquier
          // cosa de tres letras.
          if (!esPrefijo && forma.length < MIN_CARACTERES) continue;
          const tope = toleranciaDe(forma, nrm);
          const d = esPrefijo ? 0 : distancia(nrm, forma, tope);
          if (d > tope) continue;
          // Ante la misma distancia gana la corrida más corta: el valor es el
          // dato, no el dato más lo que quedó pegado al lado.
          const mejora =
            !mejor || d < mejor.distancia ||
            (d === mejor.distancia && corrida.length < mejor.palabras.length);
          if (mejora) mejor = { palabras: corrida, texto, normalizado: nrm, distancia: d };
        }
      }
    }
  }
  return mejor;
}

/**
 * Cómo se normaliza lo que el OCR leyó, según qué campo es.
 *
 * `null` quiere decir que lo que hay ahí no tiene forma de ese campo —medido:
 * "27.391.444" salió "27.391 4545", nueve dígitos donde un DNI tiene siete u
 * ocho—. Eso NO anula el hallazgo: se sabe dónde está el dato, que es lo que
 * hace falta para señalarlo en la foto. Lo que no se puede es contarlo como una
 * segunda lectura, y un valor vacío es justamente lo que el consolidador
 * descarta.
 */
function valorLeido(campo: string, texto: string): string | null {
  if (campo.startsWith("fecha")) return fechaDelFrente(texto);
  if (campo === "numeroDocumento") {
    const d = texto.replace(/\D/g, "").replace(/^0+/, "");
    return d.length >= 7 && d.length <= 8 ? d : null;
  }
  const limpio = texto.normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase().replace(/[^A-Z ]/g, "").replace(/\s+/g, " ").trim();
  return limpio || null;
}

/**
 * Un valor de varias palabras, buscado palabra por palabra.
 *
 * "RODOLFO MANUEL" está impreso en un solo renglón y el OCR lo devolvió en
 * dos: las cajas salieron con 19 px de diferencia en Y sobre letras de 30, y el
 * agrupador —cuya tolerancia está ahí para no tragarse el renglón de abajo— los
 * separó. Buscar la frase entera no encuentra nada; buscar cada palabra, sí.
 *
 * Las piezas tienen que estar CERCA entre sí: dos palabras sueltas en puntas
 * opuestas de la tarjeta no son un nombre. Y si aparece una sola, se devuelve
 * esa: el consolidador ya sabe que un valor que es prefijo de otro no compite
 * con él, lo acompaña.
 */
function buscarPorPalabras(renglones: PalabraOcr[][], valor: string): Encontrado[] {
  const trozos = valor.split(/\s+/).map(norm).filter((t) => t.length >= MIN_CARACTERES);
  if (trozos.length < 2) return [];
  const piezas = trozos
    .map((t) => buscarForma(renglones, [t]))
    .filter((p): p is Encontrado => p !== null)
    .sort((a, b) => a.palabras[0].bbox.y0 - b.palabras[0].bbox.y0 || a.palabras[0].bbox.x0 - b.palabras[0].bbox.x0);

  const juntas: Encontrado[] = [];
  for (const p of piezas) {
    if (juntas.some((q) => q.palabras.some((w) => p.palabras.includes(w)))) continue;
    const anterior = juntas[juntas.length - 1];
    if (anterior) {
      const h = Math.max(1, anterior.palabras[0].bbox.y1 - anterior.palabras[0].bbox.y0);
      if (Math.abs(p.palabras[0].bbox.y0 - anterior.palabras[0].bbox.y0) > h * 2) continue;
    }
    juntas.push(p);
  }
  return juntas;
}

/**
 * Los valores esperados, ubicados en el impreso.
 *
 * Devuelve hallazgos de origen "impreso" con el valor que leyó el OCR ahí y la
 * caja donde está. Quién decide si eso confirma o contradice es el
 * consolidador, que es el único lugar donde esa regla está escrita.
 */
export function hallazgosPorValorEsperado(
  palabras: PalabraOcr[],
  esperados: ValorEsperado[]
): Hallazgo[] {
  const renglones = filas(palabras).filter((f) => !esRenglonDeMrz(f));
  const salida: Hallazgo[] = [];
  const usadas = new Set<PalabraOcr>();

  for (const { campo, valor } of esperados) {
    // Se dejan pasar las formas de tres caracteres: no se buscan por distancia,
    // pero sí como principio de una palabra ("ARG" adentro de "ARGENTINA").
    const formas = formasImpresas(valor).filter((f) => f.length >= 3);
    if (!formas.length) continue;
    const entero = buscarForma(renglones, formas);
    const piezas = entero ? [entero] : buscarPorPalabras(renglones, valor);
    if (!piezas.length) continue;
    const todas = piezas.flatMap((p) => p.palabras);
    // Dos campos no pueden ocupar las mismas palabras: sin esto, el apellido y
    // los nombres de un documento donde ambos se parecen caían en el mismo
    // renglón y se marcaba dos veces lo mismo.
    if (todas.some((w) => usadas.has(w))) continue;
    const texto = piezas.map((p) => p.texto).join(" ");
    for (const w of todas) usadas.add(w);
    salida.push({
      campo,
      valor: valorLeido(campo, texto) ?? "",
      crudo: texto,
      origen: "impreso",
      caja: unir(todas),
    });
  }
  return salida;
}
